import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Grid, Paper, Dialog, DialogActions, DialogContent, CircularProgress } from "@mui/material";
import { downloadQrCode, getAttendanceById, refreshAttendance } from "../../../services/mentorServices/MentorServices";
import { toast } from 'react-toastify';

const DailyAttendanceCard = ({ day }) => {
  const [open, setOpen] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [labDetails, setLabDetails] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (day?.lab_id) {
      fetchLabDetails();
    }
  }, [day?.lab_id]);

  useEffect(() => {
    if (open) {
      fetchQR();
    }
  }, [open, day]);

  const fetchQR = async () => {
    setQrLoading(true);
    try {
      const response = await downloadQrCode(day.dailyAttendanceId);
      if (response && response.data) {
        const blob = new Blob([response.data], { type: 'image/png' });
        const url = window.URL.createObjectURL(blob);
        setQrCode(url);
      } else {
        throw new Error('Invalid response data');
      }
    } catch (error) {
      toast.error(error?.response?.data.message || error?.message || "Failed to fetch QR code");
      setQrCode(null);
    } finally {
      setQrLoading(false);
    }
  };

  const handleViewQR = () => {
    setOpen(true);
  };

  const handleDownloadQR = async () => {
    try {
      const response = await downloadQrCode(day.dailyAttendanceId);
      if (response && response.data) {
        const blob = new Blob([response.data], { type: 'image/png' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const formattedStartTime = labDetails?.startTime ? labDetails?.startTime.replace(/\s/g, '') : 'N/A';
        const formattedEndTime = labDetails?.endTime ? labDetails?.endTime.replace(/\s/g, '') : 'N/A';
        a.download = `attendance-${labDetails?.base_coe?.name.trim() || 'Unknown'}-${labDetails?.lab_name?.trim() || 'Unknown'}-${formattedStartTime}-${formattedEndTime}.png`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("QR code downloaded successfully!");
      } else {
        throw new Error('Invalid response data');
      }
    } catch (error) {
      toast.error(error?.response?.data.message || error?.message || "Failed to download QR code");
    }
  };

  const handleRefresh = async () => {
    try {
      setLoadingData(true);
      const response = await refreshAttendance(day?.dailyAttendanceId);
      // await fetchAllAttendanceRecords();
      fetchLabDetails();
      toast.success(response?.data?.message || "Attendance refreshed successfully.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to refresh attendance. Please try again.");
    } finally {
      setLoadingData(false);
    }
  };
  const fetchLabDetails = async () => {
    try {
      const response = await getAttendanceById(day?.dailyAttendanceId);
      setLabDetails(response?.data?.data || {});
    } catch (error) {
      toast.error("Failed to fetch lab details");
    }
  };

  const handleClose = () => {
    if (qrCode) {
      window.URL.revokeObjectURL(qrCode);
      setQrCode(null);
    }
    setOpen(false);
    setQrLoading(false);
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, maxWidth: 500, margin: "auto" }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        View Daily Attendance - {day?.dailyAttendanceId || "N/A"}
      </Typography>

      <Grid container spacing={1} display="flex" flexDirection="column">
        {[
          { label: "COE", value: labDetails?.coe?.name || "N/A" },
          { label: "Lab Name", value: labDetails?.lab?.lab_name || "N/A" },
          { label: "Attendance Name", value: labDetails?.attendanceName || "Daily" },
          { label: "CheckOut Status", value: labDetails?.isCheckoutEnabled ? "Enabled" : "Disabled" },
          { label: "Practice Time", value: `${labDetails?.sessionStartTime || "N/A"} - ${labDetails?.sessionEndTime || "N/A"}` },
          { label: "Attendance Code", value: labDetails?.attendanceCode || "N/A" },
        ].map(({ label, value }) => (
          <Grid
            item
            xs={12}
            key={label}
            display="flex"
            justifyContent="space-between"
            sx={{ flexWrap: "wrap" }}
          >
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ fontSize: { xs: "0.9rem", sm: "2rem" } }}
            >
              {label}:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                wordBreak: "break-word",
                textAlign: "right",
                fontSize: { xs: "0.8rem", sm: "2rem" },
                maxWidth: "60%" 
              }}
            >
              {value}
            </Typography>
          </Grid>
        ))}
      </Grid>

      <Box
        mt={2}
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          gap: 1,
          overflowX: "auto",
          whiteSpace: "nowrap",
          padding: 1,
          background: "#fff",
          borderRadius: 2,
          maxWidth: "100%",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleViewQR}
          sx={{
            fontSize: { xs: "0.6rem", sm: "0.65rem" },
            padding: { xs: "3px 6px", sm: "4px 8px" },
            minWidth: 80,
          }}
        >
          View QR
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleRefresh}
          sx={{
            fontSize: { xs: "0.6rem", sm: "0.65rem" },
            padding: { xs: "3px 6px", sm: "4px 8px" },
            minWidth: 80,
          }}
        >
          Refresh QR
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleDownloadQR}
          sx={{
            fontSize: { xs: "0.6rem", sm: "0.65rem" },
            padding: { xs: "3px 6px", sm: "4px 8px" },
            minWidth: 80,
          }}
        >
          Download QR
        </Button>
      </Box>




      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogContent sx={{ p: 0, width: "100%", display: "flex", justifyContent: "center" }}>
          {qrLoading ? <CircularProgress /> : qrCode ? <img src={qrCode} alt="QR Code" style={{ width: '100%' }} /> : <Typography>No QR code available</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DailyAttendanceCard;
